import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import navLogoSrc from '../../assets/mywellfie-header-logo.png';
import reportLogoSrc from '../../assets/mywellfie-full-logo.png';
import { resolveAdhocId } from '../../config/adhocTiming';
import {
  createAdhocPackCheckout,
  getAdhocPackOptions,
  type AdhocPackOption,
} from '../../api/adhocApi';
import AdhocFamilyPackSection, { type PaymentProvider } from './AdhocFamilyPackSection';
import { savePendingPackCheckout, readSavedAdhocPack } from './adhocPackStorage';
import { ADHOC_SCAN_MAINTENANCE } from '../../config/featureFlags';
import AdhocMaintenanceStage from './AdhocMaintenanceStage';
import './landing.css';

export default function AdhocFamilyPackPage() {
  const adhocId = resolveAdhocId(window.location.search);
  const displayPath = `/display?adhoc_id=${encodeURIComponent(adhocId)}`;

  const [packOptions, setPackOptions] = useState<AdhocPackOption[]>([]);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  useEffect(() => {
    setPaying(false);
  }, []);

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setPaying(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  useEffect(() => {
    getAdhocPackOptions()
      .then(setPackOptions)
      .catch(() => setPackOptions([]));
  }, []);

  const handlePayPack = useCallback(async (scanCount: number, provider: PaymentProvider) => {
    if (paying) return;
    setPaying(true);
    setPayError(null);
    try {
      const saved = readSavedAdhocPack();
      const extendFromSaved = saved?.adhocId === adhocId && saved.ownerToken
        ? {
            extend_pack_code: saved.packCode,
            extend_owner_token: saved.ownerToken,
          }
        : {};
      const { checkout_url, checkout_id, provider: checkoutProvider } = await createAdhocPackCheckout({
        adhoc_id: adhocId,
        preset: 'custom',
        custom_scans: scanCount,
        provider,
        ...extendFromSaved,
      });
      savePendingPackCheckout(adhocId, checkout_id, checkoutProvider);
      window.location.replace(checkout_url);
    } catch (e: unknown) {
      const detail = (e as { detail?: string })?.detail;
      setPayError(typeof detail === 'string' ? detail : 'Could not start checkout.');
      setPaying(false);
    }
  }, [adhocId, paying]);

  if (ADHOC_SCAN_MAINTENANCE) {
    return (
      <AdhocMaintenanceStage
        paidAfterCheckout={false}
        onReturn={() => { window.location.href = displayPath; }}
      />
    );
  }

  return (
    <div className="adhoc-landing">
      {paying && (
        <div className="pay-overlay" aria-live="polite">
          <div className="pay-overlay-spinner" />
          <p>Preparing secure checkout…</p>
        </div>
      )}

      <nav>
        <div className="wrap">
          <div className="nav-row-split">
            <div className="nav-logo">
              <img src={navLogoSrc} alt="MyWellfie — Smart scans. Health insights." />
            </div>
            <Link to={displayPath} className="btn btn-ghost family-pack-nav-back">
              <ArrowLeft size={16} aria-hidden />
              Back
            </Link>
          </div>
        </div>
      </nav>

      <main className="family-pack-page-main">
        <div className="wrap family-pack-page-inner">
          {packOptions.length > 0 ? (
            <AdhocFamilyPackSection
              options={packOptions}
              paying={paying}
              payError={payError}
              onPayPack={handlePayPack}
            />
          ) : (
            <p className="family-pack-page-lede">Loading pack options…</p>
          )}
        </div>
      </main>

      <footer>
        <div className="wrap">
          <div className="brand">
            <img src={reportLogoSrc} alt="MyWellfie" />
          </div>
          <span className="fine">
            MyWellfie reports are for informational purposes only and don&apos;t replace
            professional medical advice.
          </span>
        </div>
      </footer>
    </div>
  );
}
